@if (visible) {
  <mj-dialog
    [Visible]="true"
    [Title]="dialogTitle"
    [Width]="500"
    (Close)="onCancel()">
    <div class="share-dialog-content">
      <!-- Loading state -->
      @if (loading) {
        <div class="loading-container">
          <mj-loading text="Loading shares..."></mj-loading>
        </div>
      }
      @if (!loading) {
        <!-- Add new share section -->
        @if (canModifyShares) {
          <div class="add-share-section">
            <h4 class="section-title">Add people or roles</h4>
            <!-- Search type toggle -->
            <div class="search-type-toggle">
              <button mjButton
                [variant]="searchType === 'User' ? 'primary' : 'outline'"
                (click)="setSearchType('User')">
                <i class="fa-solid fa-user"></i> Users
              </button>
              <button mjButton
                [variant]="searchType === 'Role' ? 'primary' : 'outline'"
                (click)="setSearchType('Role')">
                <i class="fa-solid fa-users"></i> Roles
              </button>
            </div>
            <!-- Search input -->
            <div class="search-container">
              <div class="search-input-wrapper">
                <i class="fa-solid fa-search search-icon"></i>
                <input type="text"
                  class="mj-input"
                  [value]="searchText"
                  (input)="onSearchInput($any($event.target).value)"
                  [placeholder]="'Search for ' + (searchType === 'User' ? 'users' : 'roles') + '...'"
                  (blur)="showSearchResults = false" />
              </div>
              <!-- Search results dropdown -->
              @if (showSearchResults && searchResults.length > 0) {
                <div class="search-results">
                  @for (recipient of searchResults; track recipient) {
                    <div class="search-result-item"
                      (mousedown)="selectRecipient(recipient)">
                      <i [class]="getShareTypeIcon(recipient.type)"></i>
                      <div class="recipient-info">
                        <span class="recipient-name">{{ recipient.name }}</span>
                        @if (recipient.email) {
                          <span class="recipient-email">{{ recipient.email }}</span>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
            <!-- Selected recipient and permission -->
            @if (selectedRecipient) {
              <div class="selected-recipient">
                <div class="recipient-chip">
                  <i [class]="getShareTypeIcon(selectedRecipient.type)"></i>
                  <span>{{ selectedRecipient.name }}</span>
                  <button mjButton variant="flat" size="sm" (click)="clearSearch()">
                    <i class="fa-solid fa-times"></i>
                  </button>
                </div>
                <mj-dropdown
                  [Data]="permissionOptions"
                  TextField="label"
                  ValueField="value"
                  [(ngModel)]="selectedPermission"
                  [ValuePrimitive]="true"
                  class="permission-dropdown">
                  <ng-template #mjDropdownItem let-dataItem>
                    <div class="permission-option">
                      <span class="permission-label">{{ dataItem.label }}</span>
                      <span class="permission-desc">{{ dataItem.description }}</span>
                    </div>
                  </ng-template>
                </mj-dropdown>
                <button mjButton
                  variant="primary"
                  [disabled]="saving"
                  (click)="addShare()">
                  <i class="fa-solid fa-plus"></i> Add
                </button>
              </div>
            }
          </div>
        }
        <!-- Divider -->
        @if (currentShares.length > 0 && canModifyShares) {
          <hr class="section-divider">
        }
        <!-- Current shares section -->
        @if (currentShares.length > 0) {
          <div class="current-shares-section">
            <h4 class="section-title">People with access</h4>
            <div class="share-list">
              @for (share of currentShares; track share) {
                <div class="share-item">
                  <div class="share-info">
                    <i [class]="getShareTypeIcon(share.type)" class="share-type-icon"></i>
                    <div class="share-details">
                      <span class="share-name">{{ share.recipientName }}</span>
                      @if (share.recipientEmail) {
                        <span class="share-email">{{ share.recipientEmail }}</span>
                      }
                      @if (share.type === 'Role') {
                        <span class="share-type-label">Role</span>
                      }
                    </div>
                  </div>
                  <div class="share-actions">
                    @if (canModifyShares) {
                      <mj-dropdown
                        [Data]="permissionOptions"
                        TextField="label"
                        ValueField="value"
                        [ngModel]="share.permissionLevel"
                        [ValuePrimitive]="true"
                        (ValueChange)="updateSharePermission(share, $any($event))"
                        [Disabled]="saving"
                        class="permission-dropdown-small">
                      </mj-dropdown>
                    }
                    @if (!canModifyShares) {
                      <span class="permission-badge">
                        {{ getPermissionLabel(share.permissionLevel) }}
                      </span>
                    }
                    @if (canModifyShares) {
                      <button mjButton
                        variant="danger"
                        size="sm"
                        title="Remove access"
                        [disabled]="saving"
                        (click)="removeShare(share)">
                        <i class="fa-solid fa-trash"></i>
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
        <!-- Empty state -->
        @if (currentShares.length === 0 && !loading) {
          <mj-empty-state
            Icon="fa-solid fa-share-nodes"
            Title="Not shared yet"
            [Message]="EmptyShareMessage"
            Size="compact" />
        }
        <!-- Read-only notice -->
        @if (!canModifyShares) {
          <div class="readonly-notice">
            <i class="fa-solid fa-info-circle"></i>
            <span>Only the list owner can manage sharing.</span>
          </div>
        }
      }
    </div>
    <!-- Notify-by-email toggle (mockup 15). Sits above the action footer so
         the user can flip it after adding shares but before clicking Done. -->
    @if (canModifyShares) {
      <div class="notify-toggle">
        <input id="share-notify" type="checkbox" [(ngModel)]="notifyByEmail" />
        <label for="share-notify">
          <i class="fa-solid fa-envelope"></i>
          Send email notification to new shares
        </label>
      </div>
    }
    <mj-dialog-actions>
      <button mjButton variant="primary" (click)="onDone()">Done</button>
      <button mjButton variant="outline" (click)="manageInvitations.emit()">
        <i class="fa-solid fa-envelope-open-text"></i>
        Manage Invitations
      </button>
      <button mjButton (click)="onCancel()">Cancel</button>
      <a class="audit-log-link" (click)="viewAuditLog.emit(); $event.preventDefault()" href="#">
        <i class="fa-solid fa-clock-rotate-left"></i>
        View audit log
      </a>
    </mj-dialog-actions>
  </mj-dialog>
}
